import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { Fetchexecutequery } from "../../../services/process/BRS1/brsService7";

// ── Design tokens ─────────────────────────────────────────────
const T = {
  navy:      "#1E3A5F",
  navy2:     "#142B4A",
  navyHL:    "#2A4F7C",
  amber:     "#BA7517",
  amberBg:   "#FAEEDA",
  blue:      "#185FA5",
  blueLt:    "#378ADD",
  red:       "#A32D2D",
  redBg:     "#FCEBEB",
  green:     "#3B6D11",
  greenBg:   "#EAF3DE",
  border:    "rgba(30,58,95,0.11)",
  borderMd:  "rgba(30,58,95,0.18)",
  bg:        "#edf0f4",
  white:     "#ffffff",
  t1:        "#1E3A5F",
  t2:        "#4A5E7A",
  t3:        "#8496AC",
};

const CAT_COLORS = ["#185FA5","#BA7517","#1E3A5F","#3B6D11","#A32D2D","#6c3483","#117a65","#784212"];
const FONT = "'Segoe UI', system-ui, sans-serif";
const FY_OPTS = ["FY 2023–24","FY 2024–25","FY 2025–26","FY 2026–27"];

// ── Formatters ────────────────────────────────────────────────
const fmt    = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtCr  = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return "₹" + (v / 10000000).toFixed(1) + "Cr";
  if (v >= 100000)   return "₹" + (v / 100000).toFixed(1) + "L";
  return fmt(v);
};
const fmtCrY = (v) => {
  if (v >= 10000000) return "₹" + (v / 10000000).toFixed(0) + "Cr";
  if (v >= 100000)   return "₹" + (v / 100000).toFixed(0) + "L";
  return "₹" + v;
};

function statusFor(util) {
  if (util > 100) return { label: "Breached", v: "breach",  color: T.red   };
  if (util >= 90) return { label: "Warning",  v: "warn",    color: T.amber };
  if (util >= 70) return { label: "Nominal",  v: "nominal", color: T.blue  };
  return               { label: "Surplus",  v: "surplus", color: T.green };
}

// ── Pill ──────────────────────────────────────────────────────
const PILL_STYLES = {
  breach:  { bg: T.redBg,   color: T.red   },
  warn:    { bg: T.amberBg, color: T.amber },
  nominal: { bg: "#eef4ff", color: T.blue  },
  surplus: { bg: T.greenBg, color: T.green },
  draft:   { bg: T.amberBg, color: T.amber },
  active:  { bg: T.greenBg, color: T.green },
};

function Pill({ util, variant }) {
  const key = variant || statusFor(util).v;
  const s   = PILL_STYLES[key] || PILL_STYLES.surplus;
  const lbl = variant ? variant : statusFor(util).label;
  return (
    <span style={{
      display: "inline-block", padding: "2px 7px", borderRadius: 3,
      fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
      background: s.bg, color: s.color,
    }}>
      {lbl}
    </span>
  );
}

// ── MiniBar ───────────────────────────────────────────────────
function MiniBar({ pct, color, width = 60 }) {
  const w = Math.min(Number(pct) || 0, 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
      <div style={{ width, height: 4, background: "#dde3ea", borderRadius: 2, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 10, color, fontWeight: 700, minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {Number(pct).toFixed(1)}%
      </span>
    </div>
  );
}

// ── Sparkline ─────────────────────────────────────────────────
function Spark({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={28}>
      <LineChart data={data} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Tooltips ──────────────────────────────────────────────────
function DonutTip({ active, payload, totalAllocated }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const pct = totalAllocated > 0 ? ((value / totalAllocated) * 100).toFixed(1) : 0;
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 5, padding: "7px 11px", fontFamily: FONT, fontSize: 11 }}>
      <div style={{ fontWeight: 700, color: T.t1, marginBottom: 2 }}>{name}</div>
      <div style={{ color: T.blue }}>{fmt(value)}</div>
      <div style={{ color: T.t3 }}>{pct}% of allocated</div>
    </div>
  );
}

function BarTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 5, padding: "7px 11px", fontFamily: FONT, fontSize: 11 }}>
      <div style={{ fontWeight: 700, color: T.t1, marginBottom: 3 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || p.fill, marginBottom: 1 }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
}

// ── Shared style objects ──────────────────────────────────────
const cardBase  = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 6 };
const cardPad   = { ...cardBase, padding: "10px 13px" };
const hdrLbl    = { fontSize: 9, fontWeight: 700, color: T.navy2, textTransform: "uppercase", letterSpacing: "0.07em" };
const tdBase    = { padding: "7px 11px", fontSize: 11, borderBottom: `1px solid #f0f2f5`, fontVariantNumeric: "tabular-nums" };
const thBase    = { padding: "7px 11px", fontSize: 9, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: `1px solid ${T.navyHL}`, background: T.navy, whiteSpace: "nowrap" };
const selSt     = {
  padding: "4px 20px 4px 8px", border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4, fontSize: 12, fontFamily: FONT, cursor: "pointer",
  background: "rgba(255,255,255,0.09)", color: "#fff",
  appearance: "none", WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 10 10'%3E%3Cpath fill='%23ffffff99' d='M5 7L0 2h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 6px center",
  minWidth: 120, fontWeight: 600, outline: "none",
};

// ── Main ───────────────────────────────────────────────────────
export default function BudgetDashboard() {
  const [userCredentials] = useState(() => JSON.parse(sessionStorage.getItem("userCredentials")));
  const [loading,      setLoading]      = useState(true);
  const [budgetData,   setBudgetData]   = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [fy,           setFy]           = useState("FY 2025–26");
  const [entity,       setEntity]       = useState("");
  const [division,     setDivision]     = useState("");
  const [entityOpts,   setEntityOpts]   = useState([]);
  const [divisionOpts, setDivisionOpts] = useState([]);

  useEffect(() => {
    const init = async () => {
      try {
        
        const creds = Fetchexecutequery();
        const run   = async (q) => { const r = await axios.post(creds[1], { query: q }, creds[0]); return r.data || []; };
        const [entities, divisions] = await Promise.all([
          run(`SELECT entity_id AS id, name FROM user_management.entities ORDER BY name;`),
          run(`SELECT division_id AS id, name FROM user_management.divisions ORDER BY name;`),
        ]);
        setEntityOpts(entities);
        setDivisionOpts(divisions);
        if (userCredentials?.selectedentity) setEntity(String(userCredentials.selectedentity));
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    init();
  }, []);

  useEffect(() => {
    if (!entity) { setBudgetData(null); setCategoryData([]); return; }
    fetchHeader(fy, entity, division);
  }, [fy, entity, division]);

  const fetchHeader = async (fyVal, entVal, divVal) => {
    try {
      const creds = Fetchexecutequery();
      const divC  = divVal
        ? `AND bh.division_id = ${divVal}`
        : `AND (bh.division_id IS NULL OR bh.division_id = '')`;
      const res = await axios.post(creds[1], {
        query: `
          SELECT bh.id, bh.financial_year, bh.entity_id, bh.division_id,
                 bh.total_budget, bh.status, bh.created_on,
                 COALESCE(e.name,'') AS entity_name, COALESCE(d.name,'') AS division_name
          FROM budget.budget_header bh
          LEFT JOIN user_management.entities  e ON e.entity_id   = bh.entity_id
          LEFT JOIN user_management.divisions d ON d.division_id = bh.division_id
          WHERE bh.financial_year = '${fyVal}' AND bh.entity_id = ${entVal}
            ${divC} AND bh.is_active = 1 LIMIT 1
        `,
      }, creds[0]);
      if ((res.data || []).length > 0) {
        setBudgetData(res.data[0]);
        fetchCategories(res.data[0].id);
      } else {
        setBudgetData(null);
        setCategoryData([]);
      }
    } catch (e) { console.error(e); }
  };

  const fetchCategories = async (headerId) => {
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], {
        query: `
          SELECT bc.id, bc.category_name AS name, bc.amount AS budget_amount,
                 bc.from_date, bc.to_date,
                 COALESCE(SUM(bs.amount), 0) AS allocated,
                 COUNT(bs.id)                AS sub_count
          FROM budget.budget_category bc
          LEFT JOIN budget.budget_subcategory bs
            ON bs.budget_category_id = bc.id AND bs.is_active = 1
          WHERE bc.budget_header_id = ${headerId} AND bc.is_active = 1
          GROUP BY bc.id, bc.category_name, bc.amount, bc.from_date, bc.to_date
          ORDER BY bc.sort_order, bc.id
        `,
      }, creds[0]);
      setCategoryData(res.data || []);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", fontFamily: FONT, color: T.t3 }}>Loading…</div>;
  }

  // ── Derived metrics ────────────────────────────────────────
  const totalBudget    = Number(budgetData?.total_budget || 0);
  const totalAllocated = categoryData.reduce((s, c) => s + Number(c.allocated || 0), 0);
  const totalCatBudget = categoryData.reduce((s, c) => s + Number(c.budget_amount || 0), 0);
  const totalRemaining = totalBudget - totalAllocated;
  const utilizationPct = totalBudget > 0 ? (totalAllocated / totalBudget) * 100 : 0;
  const catUtilPct     = totalCatBudget > 0 ? (totalAllocated / totalCatBudget) * 100 : 0;
  const isOver         = totalAllocated > totalBudget;
  const totalSubs      = categoryData.reduce((s, c) => s + Number(c.sub_count || 0), 0);
  const sorted         = [...categoryData].sort((a, b) => Number(b.allocated) - Number(a.allocated));

  // Additional derived KPIs
  const avgCatUtil     = categoryData.length > 0
    ? categoryData.reduce((s, c) => {
        const u = c.budget_amount > 0 ? (Number(c.allocated) / Number(c.budget_amount)) * 100 : 0;
        return s + u;
      }, 0) / categoryData.length
    : 0;
  const topCat         = sorted[0] || null;
  const breachedCount  = categoryData.filter(c => Number(c.allocated) > Number(c.budget_amount)).length;
  const unbudgetedCats = categoryData.filter(c => Number(c.allocated) === 0).length;
  const budgetCoverage = totalBudget > 0 ? (totalCatBudget / totalBudget) * 100 : 0;
  const avgSubsPerCat  = categoryData.length > 0 ? (totalSubs / categoryData.length).toFixed(1) : 0;
  const catOverrun     = totalCatBudget > totalBudget ? totalCatBudget - totalBudget : 0;
  const utilColor      = isOver ? T.red : utilizationPct >= 90 ? T.amber : T.blue;

  // Sparkline — placeholder even distribution across 6 months
  const sparkData = Array.from({ length: 6 }, (_, i) => ({
    v: totalAllocated > 0 ? Math.round((totalAllocated / 6) * (i + 0.6 + (i % 2 === 0 ? 0.2 : -0.1))) : 0,
  }));

  const donutData = sorted.map(c => ({ name: c.name, value: Number(c.allocated || 0) }));
  const barData   = sorted.map(c => ({
    name:        c.name,
    "Budget":    Number(c.budget_amount || 0),
    "Allocated": Number(c.allocated || 0),
  }));

  // ── KPI definitions (2 rows × 5) ──────────────────────────
  const kpiRows = [
    [
      { label: "Department Budget",   value: fmtCr(totalBudget),        sub: budgetData?.financial_year || "—",   accent: T.navy,              over: false },
      { label: "Category Budget",     value: fmtCr(totalCatBudget),     sub: `${categoryData.length} categories`, accent: T.blue,              over: false },
      { label: "Sub-Allocated",       value: fmtCr(totalAllocated),     sub: `${utilizationPct.toFixed(1)}% of dept budget`, accent: isOver ? T.red : T.blue, over: isOver },
      { label: isOver ? "Overrun" : "Unallocated", value: fmtCr(Math.abs(totalRemaining)), sub: isOver ? "Exceeds header budget" : "Available headroom", accent: isOver ? T.red : T.green, over: isOver },
      { label: "Budget Coverage",     value: `${budgetCoverage.toFixed(1)}%`, sub: "Cat budget ÷ dept budget",   accent: T.amber,             over: false },
    ],
    [
      { label: "Dept Utilization",    value: `${utilizationPct.toFixed(1)}%`,  sub: "Allocated ÷ dept budget",   accent: utilColor,           over: isOver },
      { label: "Cat. Utilization",    value: `${catUtilPct.toFixed(1)}%`,      sub: "Allocated ÷ cat budget",    accent: T.blue,              over: false },
      { label: "Avg Cat Utilization", value: `${avgCatUtil.toFixed(1)}%`,      sub: "Mean across categories",    accent: T.navyHL,            over: false },
      { label: "Breached Categories", value: breachedCount,                    sub: `of ${categoryData.length} total`, accent: breachedCount > 0 ? T.red : T.green, over: breachedCount > 0 },
      { label: "Sub-items Total",     value: totalSubs,                        sub: `${avgSubsPerCat} avg / category`, accent: T.navy,         over: false },
    ],
  ];

  const tableColumns = [
    { h: "Category",        align: "left"   },
    { h: "Cat. Budget",     align: "right"  },
    { h: "Allocated",       align: "right"  },
    { h: "Remaining",       align: "right"  },
    { h: "Sub-items",       align: "right"  },
    { h: "% of Dept",       align: "right"  },
    { h: "% of Cat Budget", align: "right"  },
    { h: "Utilization",     align: "right"  },
    { h: "Status",          align: "center" },
  ];

  return (
    <div style={{ fontFamily: FONT, background: T.bg, minHeight: "100vh", padding: "10px 0" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "0 14px" }}>

        {/* ── Topbar ─────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", background: T.navy, borderRadius: 6, marginBottom: 8, overflow: "hidden" }}>
          <div style={{ padding: "9px 16px", borderRight: "1px solid rgba(255,255,255,0.12)", flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>Budget Dashboard</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,.45)", letterSpacing: "0.07em", textTransform: "uppercase", marginTop: 1 }}>Allocation &amp; Utilization</div>
          </div>
          <div style={{ display: "flex", alignItems: "stretch", flex: 1 }}>
            {[
              { label: "FY",       val: fy,       set: setFy,       opts: FY_OPTS.map(o => ({ id: o, name: o })), ph: null },
              { label: "Entity",   val: entity,   set: setEntity,   opts: entityOpts,   ph: "— Select —"   },
              { label: "Division", val: division, set: setDivision, opts: divisionOpts, ph: "All Divisions" },
            ].map(({ label, val, set, opts, ph }, i, arr) => (
              <div key={label} style={{ padding: "7px 14px", borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,.45)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>{label}</div>
                <select value={val} onChange={e => set(e.target.value)} style={selSt}>
                  {ph && <option value="" style={{ background: T.navy }}>{ph}</option>}
                  {opts.map(o => <option key={o.id} value={o.id} style={{ background: T.navy }}>{o.name}</option>)}
                </select>
              </div>
            ))}
          </div>
          {budgetData && (
            <div style={{ marginLeft: "auto", padding: "8px 16px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.65)" }}>
                <span style={{ fontWeight: 700, color: "#fff" }}>{budgetData.entity_name}</span>
                {budgetData.division_name && <> · <span style={{ fontWeight: 700, color: "#fff" }}>{budgetData.division_name}</span></>}
                <span style={{ color: "rgba(255,255,255,.3)" }}> #{budgetData.id}</span>
              </span>
              <Pill variant={budgetData.status === "draft" ? "draft" : "active"} util={0} />
            </div>
          )}
        </div>

        {/* ── Empty states ────────────────────────────────── */}
        {!entity ? (
          <div style={{ textAlign: "center", padding: "48px 20px", ...cardBase, color: T.t3 }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>📊</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Select an entity to begin</div>
          </div>
        ) : !budgetData ? (
          <div style={{ textAlign: "center", padding: "48px 20px", ...cardBase, color: T.t3 }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>🔍</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>No budget found for selected criteria</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Try a different financial year or division</div>
          </div>
        ) : (
          <>
            {/* ── KPI Grid — 2 rows × 5 ───────────────────── */}
            <div style={{ ...cardBase, marginBottom: 8, overflow: "hidden" }}>
              {kpiRows.map((row, ri) => (
                <div key={ri} style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)" }}>
                  {row.map(({ label, value, sub, accent, over }, i) => (
                    <div key={i} style={{
                      padding: "9px 13px",
                      borderRight: i < 4 ? `1px solid ${T.border}` : "none",
                      borderBottom: ri === 0 ? `1px solid ${T.border}` : "none",
                      borderLeft: `3px solid ${accent}`,
                    }}>
                      <div style={{ ...hdrLbl, marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: over ? T.red : T.t1, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>{value}</div>
                      <div style={{ fontSize: 10, color: T.t2, marginTop: 2 }}>{sub}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* ── Charts + Sidebar ────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "220px 1fr 195px", gap: 8, marginBottom: 8 }}>

              {/* Donut */}
              <div style={cardPad}>
                <div style={{ ...hdrLbl, marginBottom: 6 }}>Allocation mix</div>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={46} outerRadius={66}
                      paddingAngle={2} dataKey="value" labelLine={false}>
                      {donutData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} stroke="none" />)}
                    </Pie>
                    <RTooltip content={<DonutTip totalAllocated={totalAllocated} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2 }}>
                  {sorted.map((cat, i) => {
                    const pct = totalAllocated > 0
                      ? ((Number(cat.allocated) / totalAllocated) * 100).toFixed(1) : "0.0";
                    return (
                      <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: T.t2 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: CAT_COLORS[i % CAT_COLORS.length], flexShrink: 0 }} />
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.name}</span>
                        <span style={{ fontWeight: 700, color: T.t1, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bar chart */}
              <div style={cardPad}>
                <div style={{ ...hdrLbl, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  Budget vs allocated by category
                  <div style={{ display: "flex", gap: 10, fontSize: 9, color: T.t3 }}>
                    {[["Budget", "55"], ["Allocated", "cc"]].map(([l, a]) => (
                      <span key={l} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 1, background: T.blue + a, border: `1px solid ${T.blue}`, flexShrink: 0 }} />
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={165}>
                  <BarChart data={barData} barCategoryGap="28%" barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.t3, fontFamily: FONT }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: T.t3, fontFamily: FONT }} axisLine={false} tickLine={false} tickFormatter={fmtCrY} width={46} />
                    <RTooltip content={<BarTip />} />
                    <Bar dataKey="Budget" radius={[3, 3, 0, 0]}>
                      {barData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length] + "55"} stroke={CAT_COLORS[i % CAT_COLORS.length]} strokeWidth={1} />)}
                    </Bar>
                    <Bar dataKey="Allocated" radius={[3, 3, 0, 0]}>
                      {barData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length] + "cc"} stroke={CAT_COLORS[i % CAT_COLORS.length]} strokeWidth={1} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Right sidebar — stacked info cards */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

                {/* Utilization gauge */}
                <div style={cardPad}>
                  <div style={{ ...hdrLbl, marginBottom: 4 }}>Dept utilization</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: utilColor, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                    {utilizationPct.toFixed(1)}%
                  </div>
                  <div style={{ margin: "6px 0 5px", height: 5, background: "#dde3ea", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(utilizationPct, 100)}%`, background: utilColor, borderRadius: 3, transition: "width .4s" }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Pill util={utilizationPct} />
                    <span style={{ fontSize: 10, color: T.t3 }}>{fmtCr(totalAllocated)} / {fmtCr(totalBudget)}</span>
                  </div>
                </div>

                {/* Top category */}
                {topCat && (
                  <div style={cardPad}>
                    <div style={{ ...hdrLbl, marginBottom: 5 }}>Top category</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                      <span style={{ width: 3, height: 11, borderRadius: 2, background: CAT_COLORS[0], flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{topCat.name}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.blue, fontVariantNumeric: "tabular-nums" }}>{fmtCr(topCat.allocated)}</div>
                    <div style={{ fontSize: 10, color: T.t3, marginBottom: 4 }}>
                      {totalAllocated > 0 ? ((Number(topCat.allocated) / totalAllocated) * 100).toFixed(1) : "0.0"}% of total · {topCat.sub_count} sub-items
                    </div>
                    <Spark data={sparkData} color={CAT_COLORS[0]} />
                  </div>
                )}

                {/* Budget health */}
                <div style={cardPad}>
                  <div style={{ ...hdrLbl, marginBottom: 6 }}>Budget health</div>
                  {[
                    { label: "Categories",  value: categoryData.length,  color: T.navy  },
                    { label: "Breached",    value: breachedCount,        color: breachedCount > 0 ? T.red : T.green },
                    { label: "Unallocated", value: unbudgetedCats,       color: unbudgetedCats > 0 ? T.amber : T.green },
                    { label: "Avg util",    value: `${avgCatUtil.toFixed(1)}%`, color: T.blue },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: T.t3 }}>{label}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Category Table ───────────────────────────── */}
            <div style={{ ...cardBase, overflow: "hidden" }}>
              <div style={{ padding: "8px 13px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>Category Breakdown</span>
                <span style={{ fontSize: 10, color: T.t3 }}>
                  {categoryData.length} categories · {totalSubs} sub-items · sorted by allocated
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {tableColumns.map(({ h, align }, i) => (
                        <th key={i} style={{ ...thBase, textAlign: align }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((cat, idx) => {
                      const alloc     = Number(cat.allocated || 0);
                      const budget    = Number(cat.budget_amount || 0);
                      const util      = budget > 0 ? (alloc / budget) * 100 : 0;
                      const remaining = budget - alloc;
                      const pctDept   = totalBudget > 0 ? (alloc / totalBudget * 100).toFixed(1) : "0.0";
                      const pctCat    = totalCatBudget > 0 ? (alloc / totalCatBudget * 100).toFixed(1) : "0.0";
                      const isCatOver = alloc > budget;
                      const s         = statusFor(util);
                      const color     = CAT_COLORS[idx % CAT_COLORS.length];
                      return (
                        <tr key={cat.id} style={{ background: idx % 2 === 0 ? "#fafbfc" : T.white }}>
                          <td style={{ ...tdBase, textAlign: "left" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ width: 3, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
                              <span style={{ fontWeight: 600, color: T.t1 }}>{cat.name}</span>
                            </div>
                          </td>
                          <td style={{ ...tdBase, textAlign: "right", color: T.navy }}>{fmt(budget)}</td>
                          <td style={{ ...tdBase, textAlign: "right", fontWeight: 600, color: isCatOver ? T.red : T.t1 }}>{fmt(alloc)}</td>
                          <td style={{ ...tdBase, textAlign: "right", fontWeight: 600, color: remaining < 0 ? T.red : T.green }}>
                            {remaining < 0 ? "−" : ""}{fmt(Math.abs(remaining))}
                          </td>
                          <td style={{ ...tdBase, textAlign: "right", color: T.t3 }}>{cat.sub_count}</td>
                          <td style={{ ...tdBase, textAlign: "right", color: T.t3 }}>{pctDept}%</td>
                          <td style={{ ...tdBase, textAlign: "right", color: T.t3 }}>{pctCat}%</td>
                          <td style={{ ...tdBase, textAlign: "right", paddingRight: 12 }}>
                            <MiniBar pct={util} color={s.color} width={55} />
                          </td>
                          <td style={{ ...tdBase, textAlign: "center" }}>
                            <Pill util={util} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...tdBase, textAlign: "left",  fontWeight: 700, fontSize: 10, color: T.navy,  background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>TOTAL</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: T.navy,  background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>{fmt(totalCatBudget)}</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: isOver ? T.red : T.green, background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>{fmt(totalAllocated)}</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: T.t2,   background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>{fmt(Math.abs(totalBudget - totalCatBudget))}</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: T.t1,   background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>{totalSubs}</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: T.t3,   background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>—</td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, fontSize: 10, color: T.t3,   background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>—</td>
                      <td style={{ ...tdBase, textAlign: "right", paddingRight: 12,              fontSize: 10,  background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>
                        <MiniBar pct={utilizationPct} color={utilColor} width={55} />
                      </td>
                      <td style={{ ...tdBase, textAlign: "center",                               fontSize: 10,  background: "#f0f4fa", borderTop: `2px solid ${T.borderMd}` }}>
                        <Pill util={utilizationPct} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}